import { Checkbox, Table, Text, Tooltip } from '@mantine/core';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cellStyles from './Cell/Cell.module.css';
import { componentsTestIds } from '@components/testids';

/**
 * DiffReviewGrid — diff/review mode extracted from SpreadsheetEditor (TV1-1828).
 *
 * Renders a server-computed change set as a side-by-side old→new diff. It is a
 * PURE PRESENTATIONAL component: it is driven entirely by props (the change set
 * plus selection callbacks) and does NOT touch the SpreadsheetEditor's Redux
 * edit-tracking slices (cellChanges, rowMutations, etc.). The server supplies
 * BOTH the old and new value per cell, so the grid never reconstructs the prior
 * value from edit history.
 *
 * Cell status styling is reused verbatim from `Cell.module.css` (already
 * `light-dark()` dark-mode-safe — no new style classes are added here):
 *   - old column        → `.readonly` (grey hatch)   — current value, not editable
 *   - proposed change   → `.unsaved`  (pale yellow)   — new value, pending approval/apply
 *   - applied change    → `.saved`    (pale green)    — committed
 *   - failed change     → `.error`    (pale red)      — apply failed for this row
 *
 * No new cell-rendering component types are introduced — paired columns +
 * existing status colors + a leading row-selection checkbox.
 */

/**
 * A single cell's old→new pair. Mirrors the backend kernel `ChangeItem.cells`
 * value shape (`changeSet.types.ts`): `Record<colKey, { old, new }>`.
 */
export type DiffCell = {
  old: unknown;
  new: unknown;
};

/**
 * Per-target change record. Mirrors the backend kernel `ChangeItem` shape
 * (`changeSet.types.ts`):
 *   ChangeItem = { targetId, targetType, cells: Record<colKey,{old,new}>, status, note? }
 */
export type DiffChangeItem = {
  targetId: string;
  targetType: string;
  cells: Record<string, DiffCell>;
  status: 'pending' | 'approved' | 'rejected' | 'applied' | 'failed' | 'skipped';
  note?: string;
};

/**
 * Column descriptor for the diff grid. `key` matches a key in
 * `ChangeItem.cells`; `affected` columns render an adjacent old/new pair while
 * non-affected (identity/context) columns render a single read-only value
 * resolved via `getContextValue`.
 */
export type DiffColumn = {
  /** Cell key (matches `ChangeItem.cells[key]`) or a context column id. */
  key: string;
  /** Header label. */
  label: string;
  /**
   * When true, renders an adjacent old (read-only) + new column pair from
   * `cells[key]`. When false/absent, renders a single read-only context value.
   */
  affected?: boolean;
};

export type DiffReviewGridProps = {
  /** The server-computed change items to review. */
  items: DiffChangeItem[];
  /** Column layout (identity/context + affected field pairs). */
  columns: DiffColumn[];
  /** The currently approved target subset (those whose checkbox is checked). */
  approvedIds: string[];
  /** Called when a single row's selection is toggled. */
  onToggleRow: ( targetId: string, checked: boolean ) => void;
  /** Called when the header approve-all / clear checkbox is toggled. */
  onToggleAll: ( checked: boolean ) => void;
  /**
   * Resolve a read-only context (non-affected) column value for an item.
   * Defaults to reading `cells[key].old` when present.
   */
  getContextValue?: ( item: DiffChangeItem, columnKey: string ) => unknown;
};

const formatValue = ( value: unknown ): string => {
  if ( value === null || value === undefined || value === '' ) return '';
  if ( typeof value === 'string' ) return value;
  if ( typeof value === 'number' || typeof value === 'boolean' ) return String( value );
  return JSON.stringify( value );
};

/**
 * Maps a change item's status to the "new" cell's reused status style.
 * `pending`/`approved` (and the catch-all) render the proposed-change yellow;
 * `applied` renders green; `failed` renders red. `skipped`/`rejected` rows have
 * no actionable change, so the new cell stays unstyled.
 */
const newCellStatusClass = ( status: DiffChangeItem['status']): string => {
  if ( status === 'applied' ) return cellStyles.saved;
  if ( status === 'failed' ) return cellStyles.error;
  if ( status === 'skipped' || status === 'rejected' ) return '';
  return cellStyles.unsaved;
};

const DiffReviewGrid = ({
  items,
  columns,
  approvedIds,
  onToggleRow,
  onToggleAll,
  getContextValue
}: DiffReviewGridProps ) => {
  const { t } = useTranslation( 'operations' );

  const approvedSet = useMemo(() => new Set( approvedIds ), [approvedIds]);

  // A row is selectable only when it has an actionable change (not skipped/rejected).
  const selectableItems = useMemo(
    () => items.filter(( item ) => item.status !== 'skipped' && item.status !== 'rejected' ),
    [items]
  );

  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every(( item ) => approvedSet.has( item.targetId ));
  const someSelected =
    selectableItems.some(( item ) => approvedSet.has( item.targetId )) && !allSelected;

  const resolveContext = ( item: DiffChangeItem, columnKey: string ): unknown => {
    if ( getContextValue ) return getContextValue( item, columnKey );
    return item.cells[columnKey]?.old;
  };

  // colSpan for the empty state: 1 (checkbox) + affected columns count twice + context columns once.
  const totalColumns =
    1 + columns.reduce(( acc, col ) => acc + ( col.affected ? 2 : 1 ), 0 );

  return (
    <Table
      data-testid={componentsTestIds.opDiffGrid}
      highlightOnHover
      withTableBorder
      withColumnBorders
      verticalSpacing="xs"
      horizontalSpacing="md"
      styles={{
        th: {
          paddingTop: '6px',
          paddingBottom: '6px',
          fontSize: '12px',
          color: 'var(--mantine-primary-color-4)',
          fontWeight: 600
        }
      }}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th style={{ width: 40 }}>
            <Checkbox
              data-testid={componentsTestIds.opApproveAll}
              checked={allSelected}
              indeterminate={someSelected}
              onChange={( event ) => onToggleAll( event.currentTarget.checked )}
              aria-label={t( 'diff_grid_approve_all', 'Aprobar todo' )}
            />
          </Table.Th>
          {columns.map(( col ) =>
            col.affected ? (
              <Table.Th key={col.key} colSpan={2}>
                {col.label}
              </Table.Th>
            ) : (
              <Table.Th key={col.key}>{col.label}</Table.Th>
            ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 ? (
          <Table.Tr>
            <Table.Td colSpan={totalColumns}>
              <Text ta="center" fz="sm" c="dimmed" py="md">
                {t( 'diff_grid_empty', 'No hay cambios para revisar.' )}
              </Text>
            </Table.Td>
          </Table.Tr>
        ) : (
          items.map(( item ) => {
            const isSelectable =
              item.status !== 'skipped' && item.status !== 'rejected';
            const isApproved = approvedSet.has( item.targetId );
            const newClass = newCellStatusClass( item.status );

            return (
              <Table.Tr key={item.targetId} data-testid={componentsTestIds.opDiffRow( item.targetId )}>
                <Table.Td>
                  <Tooltip
                    label={item.note}
                    disabled={!item.note}
                    position="right"
                    withArrow
                  >
                    <Checkbox
                      data-testid={componentsTestIds.opRowApprove( item.targetId )}
                      checked={isApproved}
                      disabled={!isSelectable}
                      onChange={( event ) =>
                        onToggleRow( item.targetId, event.currentTarget.checked )
                      }
                      aria-label={t( 'diff_grid_approve_row', 'Aprobar fila' )}
                    />
                  </Tooltip>
                </Table.Td>
                {columns.map(( col ) => {
                  if ( !col.affected ) {
                    return (
                      <Table.Td
                        key={col.key}
                        className={cellStyles.readonly}
                        data-testid={componentsTestIds.opDiffCell( item.targetId, col.key )}
                      >
                        {formatValue( resolveContext( item, col.key ))}
                      </Table.Td>
                    );
                  }

                  const cell = item.cells[col.key];
                  return (
                    <Fragment key={col.key}>
                      <Table.Td className={cellStyles.readonly}>
                        {formatValue( cell?.old )}
                      </Table.Td>
                      <Table.Td
                        className={newClass}
                        data-testid={componentsTestIds.opDiffCell( item.targetId, col.key )}
                      >
                        {formatValue( cell?.new )}
                      </Table.Td>
                    </Fragment>
                  );
                })}
              </Table.Tr>
            );
          })
        )}
      </Table.Tbody>
    </Table>
  );
};

export default DiffReviewGrid;
